import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import Select, { components } from "react-select";
import {
  DEFAULT_RECORDS_PER_PAGE,
  PAYMENT_STATUS_OPTION,
} from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import {
  toCamelCase,
  formatDate,
} from "components/CommonComponents/CommonFunctions";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { getReceiptList, downloadReceipt } from "action/receipt";
import { getBatchList } from "action/batch";
import { getCourseNameList } from "action/course";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import CommonModal from "components/CommonComponents/CommonModal";
import { receiptList } from "constants/receipt";

const getStatusColor = (status) => {
  switch (status) {
    case "PAID":
    case "CAPTURED":
      return "success";

    case "PENDING":
      return "warning";

    case "FAILED":
      return "danger";

    default:
      return "secondary";
  }
};

const Option = (props) => (
  <components.Option {...props}>
    <Badge color={getStatusColor(props.data.value)} pill>
      {props.data.label}
    </Badge>
  </components.Option>
);
const SingleValue = (props) => (
  <components.SingleValue {...props}>
    <Badge color={getStatusColor(props.data.value)} pill>
      {props.data.label}
    </Badge>
  </components.SingleValue>
);

function ReceiptListing() {
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);
  const [pageNum, setPageNum] = useState(1);
  const [actionLoading, setActionLoading] = useState({
    id: null,
    type: null,
  });
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    fileUrl: "",
  });
  const [searchState, setSearchState] = useState({
    traineeName: "",
    receiptNumber: "",
    courseName: null,
    batchName: null,
    paymentMode: null,
    issueDate: [null, null],
    filterParams: [],
  });
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const receiptListData = useSelector((state) => state.receipt.receiptList);
  const courseNameListData = useSelector(
    (state) => state.course.courseNameList,
  );
  const batchListData = useSelector((state) => state.batch.batchList);

  useEffect(() => {
    getCourseNameList(dispatch);
    getBatchList(dispatch);
  }, [dispatch]);

  const courseOptions = useMemo(() => {
    return (
      courseNameListData?.data?.map((course) => ({
        value: course.ID,
        label: course.courseName,
      })) || []
    );
  }, [courseNameListData]);

  const batchOptions = useMemo(() => {
    return (
      batchListData?.data?.Batches?.map((batch) => ({
        value: batch.ID,
        label: batch.name,
      })) || []
    );
  }, [batchListData]);

  useEffect(() => {
    getReceiptList(dispatch, pageNum, searchState.filterParams);

    return () => {
      dispatch({ type: receiptList, payload: {} });
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  useEffect(() => {
    if (receiptListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: receiptListData?.data?.receipts?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (receiptListData?.code || receiptListData?.message) {
      setListState({
        isLoading: false,
        error:
          receiptListData?.message ||
          "Failed to load receipt list. Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [receiptListData]);

  const tableData = receiptListData?.data?.receipts?.map((item, index) => ({
    ...item,
    id: item.ID,
    srNo:
      (pageNum - 1) *
        (receiptListData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
    receiptNumber: item.receiptID || "-",
    receiptDate: item.createdAt || "-",
    payerName:
      `${item.payer?.firstName || ""} ${item.payer?.lastName || ""}`.trim() ||
      "-",
    payerType: item.payerType || "-",
    courseName: item.course?.courseName || "-",
    batchName: item.batch?.name || "-",
    amountPaid: item.payableAmount || 0,
    transactionId: item.paymentID || "-",
    status: item.paymentStatus || "-",
  }));

  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.receiptNumber?.trim()) {
      filterParams.push({
        field: "ReceiptID",
        condition: "like",
        filterValues: [searchState.receiptNumber.trim()],
      });
    }

    if (searchState.traineeName?.trim()) {
      filterParams.push({
        field: "PayerName",
        condition: "like",
        filterValues: [searchState.traineeName.trim()],
      });
    }

    if (searchState.courseName?.label) {
      filterParams.push({
        field: "CourseName",
        condition: "eq",
        filterValues: [searchState.courseName.label],
      });
    }

    if (searchState.batchName?.label) {
      filterParams.push({
        field: "BatchName",
        condition: "eq",
        filterValues: [searchState.batchName.label],
      });
    }

    if (searchState.paymentMode?.value) {
      filterParams.push({
        field: "PaymentStatus",
        condition: "eq",
        filterValues: [searchState.paymentMode.value],
      });
    }

    if (searchState.issueDate?.[0]) {
      filterParams.push({
        field: "CreatedAt",
        condition:
          searchState.issueDate[1] &&
          searchState.issueDate[0] !== searchState.issueDate[1]
            ? "btw"
            : "eq",
        filterValues: searchState.issueDate[1]
          ? [
              formatDate(searchState.issueDate[0]),
              formatDate(searchState.issueDate[1]),
            ]
          : [formatDate(searchState.issueDate[0])],
      });
    }

    return filterParams.length ? JSON.stringify(filterParams) : [];
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 60,
      maxWidth: 70,
    },
   /* {
      Header: "Receipt ID",
      accessor: "receiptNumber",
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },*/
    {
      Header: "Receipt Date",
      accessor: "receiptDate",
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
      minWidth: 190,
      maxWidth: 190,
    },
    {
      Header: "Payer Details",
      accessor: "payerName",
      minWidth: 160,
      maxWidth: 160,
      Cell: ({ row }) => {
        const { payerName, payerType } = row.original;

        return (
          <div className="text-wrapper">
            <div>{payerName || "-"}</div>
            <div>{payerType ? `(${toCamelCase(payerType)})` : "-"}</div>
          </div>
        );
      },
    },
    {
      Header: "Course Name",
      accessor: "courseName",
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Batch Name",
      accessor: "batchName",
      minWidth: 220,
      maxWidth: 220,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Amount Paid",
      accessor: "amountPaid",
      rightAlign: true,
      Cell: ({ value }) => <div className="text-wrapper">₹ {value}</div>,
    },
    {
      Header: "Transaction ID",
      accessor: "transactionId",
      minWidth: 190,
      maxWidth: 190,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      Cell: ({ value }) => (
        <Badge color={getStatusColor(value)} pill>
          {value === "CAPTURED" ? "PAID" : value}
        </Badge>
      ),
    },
    {
  Header: "Actions",
  disableSortBy: true,
  Cell: ({ row }) => {
    const data = row.original;

    return (
      <div className="d-flex gap-2 align-items-center">
        {data.paymentStatus === "CAPTURED" ? (
          <>
            {/* View */}
            <Button
              className="btn-link p-0"
              id={`view${data.id}`}
              size="sm"
              disabled={actionLoading.id === data.id}
              onClick={(e) => {
                e.stopPropagation();
                handleViewReceipt(data);
              }}
            >
              <i className="fa fa-eye" />
            </Button>

            <UncontrolledTooltip target={`view${data.id}`}>
              View
            </UncontrolledTooltip>

            {/* Download */}
            <Button
              className="btn-link p-0 ml-2"
              id={`download${data.id}`}
              size="sm"
              disabled={actionLoading.id === data.id}
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadReceipt(data);
              }}
            >
              <i className="fa fa-download" />
            </Button>

            <UncontrolledTooltip target={`download${data.id}`}>
              Download
            </UncontrolledTooltip>
          </>
        ) : (
          <span>-</span>
        )}
      </div>
    );
  },
},
  ];

  const clearSearch = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    setSearchState({
      traineeName: "",
      receiptNumber: "",
      courseName: null,
      batchName: null,
      paymentMode: null,
      issueDate: [null, null],
      filterParams: [],
    });

    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e, disable = true) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
    };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();
    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const handleDownloadReceipt = async (data) => {
    try {
      setActionLoading({ id: data.id, type: "DOWNLOAD" });
      const res = await downloadReceipt(data.id);

      if (res.status === 200 && res.data) {
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/pdf" }),
        );

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Receipt_${data.receiptNumber || data.id}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to download receipt.";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to download receipt.");
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  const handleViewReceipt = async (data) => {
    try {
      setActionLoading({ id: data.id, type: "VIEW" });
      const res = await downloadReceipt(data.id);

      if (res.status === 200 && res.data) {
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/pdf" }),
        );

        const isMobile = /Android/i.test(navigator.userAgent);

        if (isMobile) {
          window.open(url, "_blank");
        } else {
          setPreviewModal({
            isOpen: true,
            fileUrl: url,
          });
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to preview receipt.";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to preview receipt.");
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  return (
    <>
      <AdminHeader />
      {actionLoading.id && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              {actionLoading.type === "DOWNLOAD"
                ? "Downloading... Please wait."
                : "Processing... Please wait."}
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex justify-content-between">
              <CardTitle tag="h3" className="mb-0 text-default">
                  Receipts
                </CardTitle>

                {/* <div>
                  <Button className="btn-deep-blue">
                    <i className="fa-solid fa-upload mr-1 fa-lg" />
                    Export Receipts
                  </Button>
                </div> */}
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    {/* <Input
                      type="text"
                      placeholder="Search by receipt id"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.receiptNumber}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          receiptNumber: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    /> */}

                    <Input
                      type="text"
                      placeholder="Search by payer name"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.traineeName}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          traineeName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={courseOptions}
                      placeholder="Select course name"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={searchState.courseName}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          courseName: selected,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={batchOptions}
                      placeholder="Select batch name"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={searchState.batchName}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          batchName: selected,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={PAYMENT_STATUS_OPTION}
                      components={{ Option, SingleValue }}
                      placeholder="Select payment status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={searchState.paymentMode}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          paymentMode: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <DatePicker
                      onChangeRaw={(e) => e.preventDefault()}
                      selected={searchState.issueDate?.[0]}
                      onChange={(dates) =>
                        setSearchState((prev) => ({
                          ...prev,
                          issueDate: dates,
                        }))
                      }
                      startDate={searchState.issueDate?.[0]}
                      endDate={searchState.issueDate?.[1]}
                      selectsRange
                      onKeyDown={handleKeyDown}
                      isClearable={!!searchState.issueDate?.[0]}
                      placeholderText="Select receipt date or range"
                      className="form-control mr-2 mb-2 filter-input-large"
                      dateFormat="dd-MMM-yyyy"
                      showMonthDropdown
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      calendarClassName="custom-calendar"
                      onPaste={(e) => e.preventDefault()}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                        listState.showLoaderOnClick ? "search-btn" : ""
                      }`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>
                    {searchState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-deep-blue"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {receiptListData?.data?.totalRecords > 0 ? (
                    <>
                      {searchState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing {receiptListData?.data?.totalFilteredRecords}{" "}
                          of {receiptListData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {receiptListData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i class="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i class="fa-solid fa-circle-info"></i>
                    </div>
                    No receipts found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        receiptListData?.data?.totalFilteredRecords /
                          receiptListData?.data?.noOfRecordsPerPage,
                      )}
                      showPaginationBottom
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <CommonModal
        isOpen={previewModal.isOpen}
        toggle={() => setPreviewModal({ isOpen: false, fileUrl: "" })}
        title="Receipt Preview"
        size="xl"
        height="80vh"
      >
        {previewModal.fileUrl && (
          <iframe
            src={`${previewModal.fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            title="PDF Preview"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        )}
      </CommonModal>
    </>
  );
}

export default ReceiptListing;
